import './OTPInput.css'

import { clsx } from 'clsx'
import type { ClipboardEvent, KeyboardEvent, ReactElement } from 'react'
import { useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { FieldApiProps } from '#design-system/component/Form/field'
import { FieldFooter, resolveFooterMessage } from '#design-system/component/Form/FieldFooter'
import { FieldLabel } from '#design-system/component/Form/FieldLabel'
import { Tooltip } from '#design-system/component/Tooltip/Tooltip'

/** Slots are a fixed square, so the width tokens and character floor don't apply. */
export interface OTPInputProps extends Omit<FieldApiProps, 'size' | 'minChars'> {
  /** Controlled value. Shorter than `length` while the code is incomplete. */
  value: string
  /** Fires on every edit, so a consumer can submit as soon as the value reaches `length`. */
  onChange: (value: string) => void
  /** Number of slots. Default 6. */
  length?: number
  /** Focus the first slot on mount. */
  autoFocus?: boolean
  className?: string | undefined
}

const DIGITS_ONLY = /\D/g

/**
 * Segmented input for a one-time code. Each slot takes a single digit and advances on entry,
 * arrow keys and backspace walk the slots, and pasting a code fills them in one go.
 */
export const OTPInput = ({
  value,
  onChange,
  length = 6,
  autoFocus,
  label,
  helperText,
  errorMessage,
  disabledMessage,
  required,
  id,
  className,
}: OTPInputProps): ReactElement => {
  const { t } = useTranslation()

  const generatedId = useId()
  const fieldId = id ?? generatedId
  const footerId = `${fieldId}-footer`

  const slotRefs = useRef<(HTMLInputElement | null)[]>([])

  const digits = Array.from({ length }, (_, index) => value[index] ?? '')

  const focusSlot = (index: number): void => {
    const slot = slotRefs.current[index]
    slot?.focus()
    slot?.select()
  }

  const setDigit = (index: number, digit: string): void => {
    const next = [...digits]
    next[index] = digit
    onChange(next.join(''))
  }

  const handleChange = (index: number, raw: string): void => {
    const digit = raw.replace(DIGITS_ONLY, '').slice(-1)
    setDigit(index, digit)

    if (digit && index < length - 1) {
      focusSlot(index + 1)
    }
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      // Nothing to delete here, so clear the previous slot and land on it.
      event.preventDefault()
      setDigit(index - 1, '')
      focusSlot(index - 1)
      return
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusSlot(index - 1)
      return
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      focusSlot(index + 1)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(DIGITS_ONLY, '').slice(0, length)
    if (!pasted) {
      return
    }
    onChange(pasted)
    focusSlot(Math.min(pasted.length, length - 1))
  }

  const hint = resolveFooterMessage({ errorMessage, helperText })

  // The `doss-` classes below belong to the form family, which this component composes rather than
  // restyles. The lint rule guards against app code inventing its own `doss-` names.
  const rootElement = (
    <div
      // oxlint-disable-next-line doss/restricted-syntax
      className={clsx('doss-field', 'otp-input', errorMessage && 'error', className)}
      role="group"
    >
      {label !== undefined ? (
        <FieldLabel htmlFor={`${fieldId}-0`} required={required}>
          {label}
        </FieldLabel>
      ) : null}

      <div className="slots">
        {digits.map((digit, index) => {
          const slotId = `${fieldId}-${String(index)}`
          return (
            // oxlint-disable-next-line doss/restricted-syntax
            <div key={slotId} className="doss-input-shell">
              <input
                ref={(element) => {
                  slotRefs.current[index] = element
                }}
                aria-describedby={hint ? footerId : undefined}
                aria-invalid={errorMessage ? true : undefined}
                aria-label={t('auth.code_digit', {
                  index: index + 1,
                  length,
                })}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                autoFocus={autoFocus && index === 0}
                disabled={Boolean(disabledMessage)}
                id={slotId}
                inputMode="numeric"
                maxLength={1}
                onChange={(event) => {
                  handleChange(index, event.target.value)
                }}
                onFocus={(event) => {
                  event.target.select()
                }}
                onKeyDown={(event) => {
                  handleKeyDown(index, event)
                }}
                onPaste={handlePaste}
                required={required}
                value={digit}
              />
            </div>
          )
        })}
      </div>

      {hint ? (
        <FieldFooter id={footerId} kind={hint.kind}>
          {hint.text}
        </FieldFooter>
      ) : null}
    </div>
  )

  return disabledMessage !== undefined ? (
    <Tooltip title={disabledMessage}>{rootElement}</Tooltip>
  ) : (
    rootElement
  )
}
